import React, { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AuthInfo, IUser } from 'types';
import { USER_TYPE } from 'constants/enums';
import ROUTES from 'constants/routes';
import Panel from 'components/Panel/Panel';
import AuthEntryLayout from 'layouts/AuthEntryLayout/AuthEntryLayout';
import entryStyles from 'layouts/AuthEntryLayout/AuthEntryLayout.module.scss';
import { getProfileByToken } from 'utils/tokenProfile';
import { PasswordUpdate } from 'containers/PasswordUpdate/PasswordUpdate';
import { PhoneVerification } from './containers/PhoneVerification/PhoneVerification';
import { ProfileVerification } from './containers/ProfileVerification/ProfileVerification';

import styles from './PasswordRecovery.module.scss';

enum STEP {
  'PhoneVerification',
  'ProfileVerification',
  'PasswordUpdate',
  'ULSuccess'
}

const PasswordRecoveryPage: FC = () => {
  const navigate = useNavigate();

  const [tempAccessToken, setTempAccessToken] = useState<string>();
  const [step, setStep] = useState<STEP>(STEP.PhoneVerification);

  const handleBack = () => {
    switch (step) {
      case STEP.PhoneVerification:
        navigate(ROUTES.LOGIN);
        break;
      default:
        setStep(STEP.PhoneVerification);
        setTempAccessToken(undefined);
    }
  };

  const profile = useMemo<IUser | null>(() => {
    return getProfileByToken(tempAccessToken);
  }, [tempAccessToken]);

  useEffect(() => {
    if (step !== STEP.PhoneVerification || !profile) return;
    if (profile.type === USER_TYPE.UL) { // 4 = UserType.UL
        setStep(STEP.ULSuccess);
      } else {
        setStep(STEP.ProfileVerification);
      }
  }, [step, profile]);

  const handleProfileVerified = () => {
    setStep(STEP.PasswordUpdate);
  };

  const handleProfileCancel = () => {
    navigate(ROUTES.LOGIN)
  };

  const handlePhoneVerified = ({ token }: AuthInfo) => {
    setTempAccessToken(token);
  }

  if (step === STEP.PhoneVerification) {
    return <PhoneVerification onFinish={handlePhoneVerified} />;
  }

  if (step === STEP.ProfileVerification && profile && tempAccessToken) {
    return (
      <ProfileVerification
        profile={profile}
        accessToken={tempAccessToken}
        onCancel={handleProfileCancel}
        onNext={handleProfileVerified}
      />
    );
  }

  if (step === STEP.PasswordUpdate && tempAccessToken) {
    return (
      <AuthEntryLayout title="Восстановление пароля">
        <PasswordUpdate accessToken={tempAccessToken} appearance="login" />
        <button className={entryStyles.back} type="button" onClick={handleBack}>← Вернуться</button>
      </AuthEntryLayout>
    );
  }

  return (
    <Panel>
      <div className={styles.title}>Восстановление пароля</div>
      {step === STEP.ULSuccess && <div className={styles.text}>Заявка отправлена менеджеру. Ожидайте обратную связь</div>}
      {step !== STEP.ProfileVerification &&
        <div onClick={handleBack} className={styles.back}>← Вернуться</div>}
    </Panel>
  );
};

export default PasswordRecoveryPage;
